import React, { useCallback, forwardRef, memo, useState, useMemo } from 'react';
import { Rifm } from 'rifm';
import { Input } from '../input/newInput/NewInput';
import { InputType } from '../input/newInput/models';
import { MoneyInputProps } from './models';

// Duplication of the code exists in common money.ts
const numberAccept = /[\d.]+/g;

const parseNumber = (string) => (string?.match(numberAccept) || []).join('');

const formatFloatingPointNumber = (value: string, maxDigits: number, showFullDecimalNumber: boolean) => {
  const parsed = parseNumber(value);
  const [head, tail] = parsed.split('.');
  // Avoid rounding errors at toLocaleString as when user enters 1.239 and maxDigits=2 we
  // must not to convert it to 1.24, it must stay 1.23
  const scaledTail = tail != null ? tail.slice(0, maxDigits) : '';

  const number = Number.parseFloat(`${head}.${scaledTail}`);

  if (Number.isNaN(number)) {
    return '';
  }

  const formatted = number.toLocaleString('en-US', {
    minimumFractionDigits: showFullDecimalNumber ? 2 : 0,
    maximumFractionDigits: showFullDecimalNumber ? 2 : maxDigits,
  });

  if (parsed.includes('.')) {
    const [formattedHead, formattedTail] = formatted.split('.');

    return `${formattedHead}.${formattedTail || ''}`;
  }

  return formatted;
};

// Duplication of the code exists in common money.ts
const formatCurrency = (showFullDecimalNumber: boolean, hasPlaceholder: boolean) => (value: string) =>
  (!hasPlaceholder ? '$' : '') + formatFloatingPointNumber(value, 2, showFullDecimalNumber);

const MoneyInput = forwardRef(
  (
    {
      onClickPreventDefault: onClick,
      value,
      onChange: _onChange,
      showFullDecimalNumber,
      placeholder,
      ...props
    }: MoneyInputProps,
    ref: any
  ) => {
    const hasPlaceholder = !!placeholder;

    const handleOnChange = useCallback(
      (newAmount: string) => {
        _onChange(parseNumber(newAmount), props.name);
      },
      [_onChange, props.name]
    );

    const format = useCallback(formatCurrency(showFullDecimalNumber, hasPlaceholder), [
      showFullDecimalNumber,
      hasPlaceholder,
    ]);

    return (
      <Rifm
        {...{
          accept: numberAccept,
          format,
          value,
          onChange: handleOnChange,
        }}
      >
        {({ value, onChange }: { value: string; onChange: any }) => {
          return (
            <Input
              {...{
                ...props,
                innerRef: ref,
                value,
                onChange,
                placeholder,
                type: InputType.Money,
                onClick,
              }}
            />
          );
        }}
      </Rifm>
    );
  }
);

const MemoMoneyInput = memo(MoneyInput);
export { MemoMoneyInput as MoneyInput };
